import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Button,
  Checkbox,
  Empty,
  Input,
  Modal,
  Select,
  Space,
  Spin,
  Tag,
  Typography,
} from "antd";
import {
  ArrowLeftOutlined,
  SearchOutlined,
  TeamOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { fetchData, postResponseData } from "../../api/apiService";
import { CLIENTS } from "../../api/endpointMap";
import { useOrgUsers } from "../../context/OrgUsersContext";
import { PORTAL_TO_FLAG } from "../UserAssignmentSelect";

// Sentinel id for the "Move to Shared pool" pseudo-user that appears at the
// top of the user picker dropdown. Chosen so it can't collide with any real
// user UUID. Triggers the Shared branch in selectedUser memo + downstream.
const SHARED_OPTION_ID = "__SHARED__";

// Bulk Assign dialog (USER_ASSIGN_CHANGES_PLAN.md §1.1, FE Phase 2A).
//
// Two-step flow:
//   Step 1 — pick ONE assignable user from OrgUsersContext (which Phase
//            1.B already filtered to role=user).
//   Step 2 — across IT / GST / TDS, pick clients that are NOT already
//            owned by the selected user. Per-section search + select-all.
//
// Submit fans out to up to three independent
// `POST /clients/{portal}/bulk-assign` calls in parallel via
// Promise.allSettled — any one failing doesn't block the others. The
// admin sees a single toast collating the result.
//
// Why no `?exclude_assigned_user=...` query param: the BE does not yet
// expose one, so we fetch the full per-portal list and filter
// `assigned_user_id !== selectedUserId` client-side. We paginate via
// `page=1, 2, 3, …` until the BE returns a short page so orgs with
// 100+ clients per portal get the full assignable list (not just the
// first 100). Slightly chattier — N pages per accessible portal — but
// decouples the FE from BE deploy ordering. Same rationale as the
// Phase 1.B FE-side-filter Approach A.
//
// Why client-side typeahead (not debounced HTTP search): the per-portal
// fetch returns the full org-scoped list (typical orgs <500 clients per
// portal). The Antd Input filter is sub-millisecond at that size; an
// HTTP debounce would be slower AND chattier. Same pattern as
// <UserAssignmentSelect />'s typeahead.

const { Text } = Typography;

const PORTALS = [
  {
    key: "income-tax",
    label: "Income Tax",
    listUrl: CLIENTS.INCOME_TAX_LIST,
    // Per-portal id-ish fields used in addition to client_name when the
    // admin types into the search box. Each value here is a row property
    // that may be present; we search across whichever ones are non-empty.
    searchFields: ["client_name", "username", "pan_no"],
  },
  {
    key: "gst",
    label: "GST",
    listUrl: CLIENTS.GST_LIST,
    searchFields: ["client_name", "gst_username", "username", "gstin"],
  },
  {
    key: "tds",
    label: "TDS",
    listUrl: CLIENTS.TDS_LIST,
    searchFields: ["client_name", "tds_username", "username", "tan_number"],
  },
];

// Page size for the per-portal fetch. The BE caps at 100; we loop
// sequentially via `page=1, 2, 3, …` until the response is short
// (rawRows < PAGE_SIZE) or we reach `total_pages`, so orgs with 100+
// clients per portal also get the full assignable list in step 2 —
// not just the first page. MAX_PAGES is a sanity cap (5000 clients)
// to defend against a runaway fetch on a misbehaving BE response.
const PAGE_SIZE = 100;
const MAX_PAGES = 50;

const initials = (user) => {
  const source =
    user?.full_name || user?.first_name || user?.email || "";
  return (source.charAt(0) || "?").toUpperCase();
};

const matchesSearch = (row, term, searchFields) => {
  if (!term) return true;
  const needle = term.toLowerCase();
  for (const field of searchFields) {
    const value = row?.[field];
    if (value && String(value).toLowerCase().includes(needle)) return true;
  }
  return false;
};

const PortalSection = ({
  portal,
  rows,
  loading,
  search,
  setSearch,
  selectedIds,
  onToggle,
  onToggleAll,
}) => {
  const filtered = useMemo(
    () => rows.filter((r) => matchesSearch(r, search, portal.searchFields)),
    [rows, search, portal.searchFields]
  );
  // "Select all visible" only acts on the currently-filtered subset —
  // matches the spec's "select all visible" wording. If the admin types
  // a search and then ticks the all-checkbox, only those rows are
  // selected; clearing the search later leaves them selected.
  const filteredIds = useMemo(() => filtered.map((r) => r.id), [filtered]);
  const allChecked =
    filteredIds.length > 0 && filteredIds.every((id) => selectedIds.has(id));
  const someChecked =
    !allChecked && filteredIds.some((id) => selectedIds.has(id));

  return (
    <div className="border rounded-md p-3 mb-3 bg-white">
      <div className="flex items-center justify-between mb-2 gap-2">
        <div className="flex items-center gap-2">
          <Text strong>{portal.label}</Text>
          <Tag color="default" className="text-xs">
            {selectedIds.size > 0
              ? `${
                  filtered.filter((r) => selectedIds.has(r.id)).length
                } / ${filtered.length} selected`
              : `${filtered.length} available`}
          </Tag>
        </div>
        <Checkbox
          checked={allChecked}
          indeterminate={someChecked}
          onChange={(e) => onToggleAll(filteredIds, e.target.checked)}
          disabled={filteredIds.length === 0}
        >
          Select all visible
        </Checkbox>
      </div>
      <Input
        prefix={<SearchOutlined />}
        placeholder={`Search ${portal.label} clients`}
        allowClear
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="mb-2"
        size="small"
      />
      <div
        style={{
          maxHeight: 220,
          overflowY: "auto",
          borderTop: "1px solid #f0f0f0",
        }}
      >
        {loading ? (
          <div className="py-6 text-center">
            <Spin />
          </div>
        ) : filtered.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              search
                ? "No matches"
                : "No assignable clients in this portal"
            }
            className="py-3"
          />
        ) : (
          filtered.map((row) => {
            const checked = selectedIds.has(row.id);
            // Many-to-many: render each current assignee as its own tag;
            // an empty `assigned_users` array means the client is Shared.
            const assignees = Array.isArray(row.assigned_users)
              ? row.assigned_users
              : [];
            const sharedOrAssignee =
              assignees.length > 0 ? (
                <span className="flex flex-wrap gap-1 justify-end">
                  {assignees.map((u) => (
                    <Tag color="blue" className="text-xs" key={u.user_id}>
                      {u.user_name || u.user_email || "Assigned"}
                      {u.user_name && u.user_email && ` (${u.user_email})`}
                    </Tag>
                  ))}
                </span>
              ) : (
                <Tag className="text-xs">Shared</Tag>
              );
            return (
              <label
                key={row.id}
                className="flex items-center gap-2 py-1 px-1 cursor-pointer hover:bg-slate-50"
              >
                <Checkbox
                  checked={checked}
                  onChange={() => onToggle(row.id)}
                />
                <span className="flex-1 truncate text-sm">
                  {row.client_name || row.username || row.id}
                </span>
                {sharedOrAssignee}
              </label>
            );
          })
        )}
      </div>
    </div>
  );
};

const BulkAssignDialog = ({ open, onClose, onSuccess, portal = null }) => {
  const { users, loaded, loading, isSingleUserOrg, ensureLoaded } =
    useOrgUsers();
  // Page-scope: when the dialog is opened from a specific portal page
  // (IT / GST / TDS), restrict BOTH the step-1 user picker AND the
  // step-2 sections to that portal only. Falls back to cross-portal
  // behaviour (full user list + selectedUser-derived sections) when no
  // page portal is supplied.
  const pageFlag = portal ? PORTAL_TO_FLAG[portal] || null : null;
  const assignableUsers = pageFlag
    ? users.filter((u) => u[pageFlag] !== false)
    : users;
  const [step, setStep] = useState("user"); // "user" | "clients"
  const [selectedUserId, setSelectedUserId] = useState(null);

  // Per-portal client lists fetched lazily on entry to step 2.
  const [portalRows, setPortalRows] = useState({
    "income-tax": [],
    gst: [],
    tds: [],
  });
  const [portalLoading, setPortalLoading] = useState({
    "income-tax": false,
    gst: false,
    tds: false,
  });
  const [portalSearch, setPortalSearch] = useState({
    "income-tax": "",
    gst: "",
    tds: "",
  });
  // Per-portal selected client-id Sets. Object keyed by portal key.
  const [selected, setSelected] = useState({
    "income-tax": new Set(),
    gst: new Set(),
    tds: new Set(),
  });

  const [submitting, setSubmitting] = useState(false);

  // Reset all internal state on every fresh open. Without this, a previous
  // user/selection persists and confuses the admin on re-open.
  useEffect(() => {
    if (!open) return;
    ensureLoaded();
    setStep("user");
    setSelectedUserId(null);
    setPortalRows({ "income-tax": [], gst: [], tds: [] });
    setPortalLoading({ "income-tax": false, gst: false, tds: false });
    setPortalSearch({ "income-tax": "", gst: "", tds: "" });
    setSelected({
      "income-tax": new Set(),
      gst: new Set(),
      tds: new Set(),
    });
    setSubmitting(false);
  }, [open, ensureLoaded]);

  const selectedUser = useMemo(() => {
    // Shared pool sentinel — return a virtual "user-shape" object with
    // isShared=true so downstream code can branch on it without breaking
    // existing user-derived rendering paths.
    if (selectedUserId === SHARED_OPTION_ID) {
      return { id: SHARED_OPTION_ID, isShared: true };
    }
    return users.find((u) => u.id === selectedUserId) || null;
  }, [users, selectedUserId]);

  // Only show / fetch portal sections relevant to this dialog instance:
  //   1. If `portal` prop is set (dialog opened from a specific portal
  //      page) → restrict to that single portal regardless of selected
  //      user. Step-1 filter has already narrowed users to that portal.
  //   2. Otherwise → fall back to selectedUser-derived sections (mirrors
  //      single-row Reassign's per-user filter).
  //   3. If no selectedUser yet (step 1) → return full PORTALS for safe
  //      render before user is picked.
  const accessiblePortals = useMemo(() => {
    if (portal) {
      return PORTALS.filter((p) => p.key === portal);
    }
    if (!selectedUser) return PORTALS;
    // Shared mode — every portal's clients can move to the Shared pool, so
    // don't filter by per-user portal flags.
    if (selectedUser.isShared) return PORTALS;
    return PORTALS.filter((p) => {
      const flag = PORTAL_TO_FLAG[p.key];
      return !flag || selectedUser[flag] !== false;
    });
  }, [portal, selectedUser]);

  const totalSelected =
    selected["income-tax"].size + selected.gst.size + selected.tds.size;

  // Step 2 entry — fetch only the portals the selected user has access
  // to. Skipping inaccessible portals avoids unnecessary list calls and
  // matches the hidden UI sections so we never end up with fetched-but-
  // never-rendered rows in state.
  const enterStepClients = async () => {
    if (!selectedUserId) return;
    setStep("clients");
    setPortalLoading((prev) =>
      accessiblePortals.reduce(
        (acc, p) => ({ ...acc, [p.key]: true }),
        prev,
      ),
    );
    await Promise.allSettled(
      accessiblePortals.map(async (portal) => {
        try {
          // Loop through every page until the BE signals "done" — either
          // the returned page is short (< PAGE_SIZE) or we've reached
          // `total_pages`. Without this, orgs with 100+ clients per
          // portal saw only the first 100 in step 2 and could not
          // bulk-assign the long tail.
          const allRows = [];
          let page = 1;
          while (page <= MAX_PAGES) {
            const data = await fetchData(portal.listUrl, {
              page,
              page_size: PAGE_SIZE,
            });
            const rawRows =
              (Array.isArray(data?.items) && data.items) ||
              (Array.isArray(data?.results) && data.results) ||
              (Array.isArray(data) && data) ||
              [];
            allRows.push(...rawRows);
            const totalPages = data?.total_pages;
            if (
              rawRows.length < PAGE_SIZE ||
              (typeof totalPages === "number" && page >= totalPages)
            ) {
              break;
            }
            page += 1;
          }
          // Many-to-many: exclude clients the chosen user is ALREADY an
          // assignee of. `assigned_users` is the array of {user_id,...}
          // objects (empty = Shared). A client is assignable here unless
          // the selected user is already one of its assignees.
          //
          // Shared-mode branch: only show clients that currently have at
          // least one assignee — moving an already-Shared client to Shared
          // is a no-op, so excluding them avoids wasted user clicks.
          const filtered = allRows.filter((r) => {
            if (!r) return false;
            const assignees = Array.isArray(r.assigned_users)
              ? r.assigned_users
              : [];
            if (selectedUser?.isShared) {
              return assignees.length > 0;
            }
            return !assignees.some((u) => u.user_id === selectedUserId);
          });
          setPortalRows((prev) => ({ ...prev, [portal.key]: filtered }));
        } catch {
          // Swallow per-portal failures — surface a generic empty state
          // rather than blocking the other two portals.
          setPortalRows((prev) => ({ ...prev, [portal.key]: [] }));
        } finally {
          setPortalLoading((prev) => ({ ...prev, [portal.key]: false }));
        }
      })
    );
  };

  const handleToggle = (portalKey) => (clientId) => {
    setSelected((prev) => {
      const next = new Set(prev[portalKey]);
      if (next.has(clientId)) next.delete(clientId);
      else next.add(clientId);
      return { ...prev, [portalKey]: next };
    });
  };

  const handleToggleAll = (portalKey) => (ids, shouldCheck) => {
    setSelected((prev) => {
      const next = new Set(prev[portalKey]);
      ids.forEach((id) => {
        if (shouldCheck) next.add(id);
        else next.delete(id);
      });
      return { ...prev, [portalKey]: next };
    });
  };

  const handleSubmit = async () => {
    if (!selectedUserId || totalSelected === 0) return;
    setSubmitting(true);
    // Build the up-to-three calls — skip portals with zero selections.
    // Iterate accessiblePortals so a portal the selected user no longer
    // has access to (edge case: flags flipped between step 1 and step 2)
    // doesn't get a no-op call.
    const calls = accessiblePortals.map((portal) => {
      const ids = Array.from(selected[portal.key]);
      if (ids.length === 0) return null;
      return {
        portal,
        promise: postResponseData(CLIENTS.BULK_ASSIGN(portal.key), {
          client_ids: ids,
          // Many-to-many: send the chosen user as a single-element array.
          // This dialog's two-step flow is built around assigning to ONE
          // user, so the array always has exactly one id. Shared mode
          // sends an empty array, which BE interprets as "move to Shared
          // pool" — deletes all client_assignees rows for these clients.
          user_ids: selectedUser?.isShared ? [] : [selectedUserId],
        }),
        count: ids.length,
      };
    }).filter(Boolean);

    const results = await Promise.allSettled(calls.map((c) => c.promise));

    let totalSuccess = 0;
    const failures = [];
    const partials = [];
    results.forEach((r, i) => {
      const { portal, count } = calls[i];
      if (r.status === "fulfilled") {
        const data = r.value || {};
        const succ = Number(data?.success) || 0;
        totalSuccess += succ;
        const skipped = Array.isArray(data?.skipped) ? data.skipped : [];
        if (skipped.length > 0) {
          partials.push(
            `${portal.label}: ${succ}/${count} (${skipped.length} skipped)`
          );
        }
      } else {
        const detail =
          r.reason?.detail ||
          r.reason?.response?.data?.detail ||
          r.reason?.message ||
          "Failed";
        failures.push(
          `${portal.label}: ${typeof detail === "string" ? detail : "Failed"}`
        );
      }
    });

    setSubmitting(false);

    if (failures.length === 0 && partials.length === 0) {
      toast.success(
        selectedUser?.isShared
          ? `Moved ${totalSuccess} client${totalSuccess === 1 ? "" : "s"} to Shared pool`
          : `Assigned ${totalSuccess} client${totalSuccess === 1 ? "" : "s"} to ${
              selectedUser?.email || "user"
            }`
      );
      onSuccess?.();
      onClose?.();
      return;
    }
    if (failures.length === 0) {
      // All requests succeeded but some clients were skipped server-side
      // (out-of-scope, etc.). Treat as warning; close so the admin's
      // changes commit, but surface the partial detail.
      toast.info(
        `Assigned ${totalSuccess}; partial: ${partials.join("; ")}`
      );
      onSuccess?.();
      onClose?.();
      return;
    }
    // At least one portal call rejected. Keep the dialog open so the
    // admin can retry without losing their selection.
    const lines = [
      totalSuccess > 0 && `Assigned ${totalSuccess}`,
      partials.length > 0 && `partial: ${partials.join("; ")}`,
      failures.length > 0 && `failed: ${failures.join("; ")}`,
    ]
      .filter(Boolean)
      .join(" - ");
    toast.error(lines || "Bulk assign failed");
  };

  // ── Empty-state branch: single-user-org or zero assignable users.
  // Uses `assignableUsers` (post page-scope filter) so an org whose
  // users all lack the current portal still lands on the empty state
  // instead of an unselectable Select.
  const noAssignableUsers =
    loaded && (isSingleUserOrg || assignableUsers.length === 0);

  // ── Render ────────────────────────────────────────────────────────
  return (
    <Modal
      open={open}
      onCancel={submitting ? undefined : onClose}
      title="Bulk assign clients"
      width={680}
      destroyOnClose
      maskClosable={!submitting}
      footer={
        step === "user" ? (
          <Space>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              type="primary"
              disabled={!selectedUserId || noAssignableUsers}
              onClick={enterStepClients}
            >
              Next
            </Button>
          </Space>
        ) : (
          <Space>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => setStep("user")}
              disabled={submitting}
            >
              Back
            </Button>
            <Button onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button
              type="primary"
              loading={submitting}
              disabled={totalSelected === 0}
              onClick={handleSubmit}
            >
              {submitting
                ? selectedUser?.isShared
                  ? "Moving…"
                  : "Assigning…"
                : selectedUser?.isShared
                ? `Move ${totalSelected || ""} to Shared`.trim()
                : `Assign ${totalSelected || ""} client${
                    totalSelected === 1 ? "" : "s"
                  }`.trim()}
            </Button>
          </Space>
        )
      }
    >
      {step === "user" ? (
        <>
          <div className="mb-3 text-gray-600 text-sm">
            {accessiblePortals.length === 1 ? (
              <>
                Pick a user; in the next step, choose the{" "}
                {accessiblePortals[0].label} clients they should own.
              </>
            ) : (
              <>
                Pick a user; in the next step, choose the clients they
                should own across Income Tax, GST, and TDS.
              </>
            )}
          </div>
          {noAssignableUsers ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No assignable users in your organization yet."
              className="py-6"
            >
              <Link to="/team">
                <Button type="primary">Invite a user from Users page</Button>
              </Link>
            </Empty>
          ) : (
            <>
              <Select
                showSearch
                placeholder={loading ? "Loading users…" : "Select a user"}
                value={selectedUserId}
                onChange={setSelectedUserId}
                disabled={!loaded || loading}
                loading={loading}
                style={{ width: "100%" }}
                optionFilterProp="label"
                options={[
                  // Shared-pool option appears at the top so admins can
                  // bulk-un-assign clients (move them back to Shared) in
                  // the same flow as bulk-assign. BE accepts user_ids=[]
                  // as "move to Shared pool" — no special endpoint needed.
                  {
                    value: SHARED_OPTION_ID,
                    label: "Move to Shared pool (no specific owner)",
                  },
                  ...assignableUsers.map((u) => {
                    // Surface per-portal access in the picker so the admin
                    // sees up-front which step-2 sections will appear after
                    // selecting this user. `!== false` mirrors the same
                    // defensive check used by accessiblePortals memo.
                    const portals = [];
                    if (u.enable_income_tax_portal !== false) portals.push("IT");
                    if (u.enable_gst_portal !== false) portals.push("GST");
                    if (u.enable_tds_portal !== false) portals.push("TDS");
                    const portalStr = portals.length
                      ? ` · ${portals.join(", ")}`
                      : " · (no portal access)";
                    const namePart = u.full_name
                      ? `${u.full_name} (${u.email})`
                      : u.email;
                    return { value: u.id, label: namePart + portalStr };
                  }),
                ]}
              />
              {selectedUser && selectedUser.isShared && (
                <div className="mt-3 flex items-center gap-3 p-3 bg-slate-50 rounded">
                  <Avatar
                    icon={<TeamOutlined />}
                    style={{ backgroundColor: "#1890ff" }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">Shared pool</div>
                    <div className="text-xs text-gray-500 truncate">
                      Clients moved here are visible to everyone in your
                      organization
                    </div>
                  </div>
                  <Tag color="blue">shared</Tag>
                </div>
              )}
              {selectedUser && !selectedUser.isShared && (
                <div className="mt-3 flex items-center gap-3 p-3 bg-slate-50 rounded">
                  <Avatar icon={<UserOutlined />}>
                    {initials(selectedUser)}
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">
                      {selectedUser.full_name || selectedUser.email}
                    </div>
                    <div className="text-xs text-gray-500 truncate">
                      {selectedUser.email}
                    </div>
                  </div>
                  <Space size={4} wrap>
                    <Tag color="blue">{selectedUser.role || "user"}</Tag>
                    {selectedUser.enable_income_tax_portal !== false && (
                      <Tag color="geekblue">IT</Tag>
                    )}
                    {selectedUser.enable_gst_portal !== false && (
                      <Tag color="green">GST</Tag>
                    )}
                    {selectedUser.enable_tds_portal !== false && (
                      <Tag color="orange">TDS</Tag>
                    )}
                  </Space>
                </div>
              )}
            </>
          )}
        </>
      ) : (
        <>
          <Alert
            type="info"
            showIcon
            className="mb-3"
            message={
              selectedUser?.isShared ? (
                <span>
                  Moving selected clients to{" "}
                  <Text strong>Shared pool</Text>. Only clients with at least
                  one current assignee are listed below.
                </span>
              ) : (
                <span>
                  Assigning to{" "}
                  <Text strong>
                    {selectedUser?.full_name || selectedUser?.email || "user"}
                  </Text>
                  {selectedUser?.full_name && selectedUser?.email && (
                    <Text type="secondary"> ({selectedUser.email})</Text>
                  )}
                  . Only clients NOT already owned by them are listed below.
                </span>
              )
            }
          />
          {accessiblePortals.map((portal) => (
            <PortalSection
              key={portal.key}
              portal={portal}
              rows={portalRows[portal.key]}
              loading={portalLoading[portal.key]}
              search={portalSearch[portal.key]}
              setSearch={(v) =>
                setPortalSearch((prev) => ({ ...prev, [portal.key]: v }))
              }
              selectedIds={selected[portal.key]}
              onToggle={handleToggle(portal.key)}
              onToggleAll={handleToggleAll(portal.key)}
            />
          ))}
          <div className="text-sm text-gray-600 mt-2">
            <Text strong>{totalSelected}</Text> client
            {totalSelected === 1 ? "" : "s"} selected -{" "}
            {accessiblePortals
              .map((p) => `${selected[p.key].size} from ${p.label}`)
              .join(", ")}.
          </div>
        </>
      )}
    </Modal>
  );
};

export default BulkAssignDialog;
